import { useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Banner,
  BlockStack,
  Box,
  Button,
  InlineStack,
  Loading,
  Text,
  TextField,
  SortableList,
} from "@medentic/polaris-react";
import { PlusCircleIcon, ExternalIcon } from "@medentic/polaris-icons";
import { useTranslation } from "react-i18next";
import { Event, EventTicket, TicketFormData, EventBookType } from "@features/events/types";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { getLocalizedText } from "@shared/lib";
import { EVENT_CURRENCY_DEFAULT } from "@features/events/constants";
import { URLLinkBuilder } from "@shared/lib";
import { CopyToClipboard } from "@shared/ui";
import TicketItem from "./TicketItem";
import TicketModal from "./TicketModal";

interface TicketsListProps {
  eventData: Partial<Event>;
  isNewEvent: boolean;
}

interface TicketShareableLinkBannerProps {
  eventId: string;
  language: string;
}

function TicketShareableLinkBanner({
  eventId,
  language,
}: TicketShareableLinkBannerProps) {
  const { t } = useTranslation();
  const checkoutUrl = URLLinkBuilder.checkoutEvent(eventId, language);

  return (
    <Banner tone="info">
      <BlockStack gap="200">
        <Text as="p" variant="bodyMd">
          {t("eventDetails.payment.ticketsPublicViewDescription")}
        </Text>
        <InlineStack gap="200" blockAlign="center" wrap={false}>
          <div style={{ flex: 1 }}>
            <TextField
              label=""
              value={checkoutUrl}
              readOnly
              autoComplete="off"
            />
          </div>
          <CopyToClipboard 
            text={checkoutUrl}
            variant="secondary"
            accessibilityLabel={t("eventDetails.payment.ticketsPublicViewDescription")}
          />
          <Button
            variant="secondary"
            icon={ExternalIcon}
            onClick={() => window.open(checkoutUrl, '_blank')}
            accessibilityLabel={t("eventDetails.payment.viewCheckoutPage")}
          />
        </InlineStack>
      </BlockStack>
    </Banner>
  );
}

export default function TicketsList({ eventData, isNewEvent }: TicketsListProps) {
  const { t, i18n } = useTranslation();
  const { eventOrganizerId: hcc, eventId } = useParams<{
    eventOrganizerId: string;
    eventId: string;
  }>();

  const { POST, PUT, DELETE, PATCH } = useApiRequest();
  const queryClient = useQueryClient();

  // Modal state
  const [isTicketModalOpen, setIsTicketModalOpen] = useState(false);
  const [editingTicket, setEditingTicket] = useState<EventTicket | null>(null);

  // Ticket form state
  const [ticketForm, setTicketForm] = useState<TicketFormData>({
    title: { bg: "", en: "" },
    description: { bg: "", en: "" },
    price: 0,
    minQuantity: 1,
    isFree: false,
    hasExpiry: false,
    expiresAt: "",
    hasStart: false,
    startsAt: "",
    visibility: 1,
  });

  // Ticket CRUD mutations
  const createTicketMutation = useMutation({
    mutationFn: (ticketData: Omit<EventTicket, "id">) =>
      POST(
        `/api/healthcare-companies/${hcc}/e/events/${eventId}/tickets`,
        ticketData
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["event", hcc, eventId] });
      queryClient.invalidateQueries({ queryKey: ["events", hcc] });
      closeTicketModal();
    },
    onError: (error: unknown) => {
      console.error("Error creating ticket:", error);
    },
  });

  const updateTicketMutation = useMutation({
    mutationFn: (ticketData: EventTicket) =>
      PUT(
        `/api/healthcare-companies/${hcc}/e/events/${eventId}/tickets/${ticketData.id}`,
        ticketData
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["event", hcc, eventId] });
      queryClient.invalidateQueries({ queryKey: ["events", hcc] });
      closeTicketModal();
    },
    onError: (error: unknown) => {
      console.error("Error updating ticket:", error);
    },
  });

  const deleteTicketMutation = useMutation({
    mutationFn: (ticketId: string) =>
      DELETE(
        `/api/healthcare-companies/${hcc}/e/events/${eventId}/tickets/${ticketId}`
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["event", hcc, eventId] });
      queryClient.invalidateQueries({ queryKey: ["events", hcc] });
    },
    onError: (error: unknown) => {
      console.error("Error deleting ticket:", error);
    },
  });

  const reorderTicketsMutation = useMutation({
    mutationFn: (ticketIds: string[]) =>
      PATCH(
        `/api/healthcare-companies/${hcc}/e/events/${eventId}/tickets/reorder`,
        {
          ticketIds,
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["event", hcc, eventId] });
    },
    onError: (error: unknown) => {
      console.error("Error reordering tickets:", error);
    },
  });

  // Open ticket modal
  const openTicketModal = useCallback(
    (ticket?: EventTicket, duplicateCase?: boolean) => {
      if (ticket) {
        setEditingTicket(duplicateCase ? null : ticket);
        setTicketForm({
          title: ticket.title,
          description: ticket.description || { bg: "", en: "" },
          price: ticket.price,
          minQuantity: ticket.minQuantity,
          isFree: ticket.price === 0,
          hasExpiry: Boolean(ticket.expiresAt),
          expiresAt: ticket.expiresAt || "",
          hasStart: Boolean(ticket.startsAt),
          startsAt: ticket.startsAt || "",
          visibility: ticket.visibility,
        });
      } else {
        setEditingTicket(null);
        setTicketForm({
          title: { bg: "", en: "" },
          description: { bg: "", en: "" },
          price: 0,
          minQuantity: 1,
          isFree: false,
          hasExpiry: false,
          expiresAt: "",
          hasStart: false,
          startsAt: "",
          visibility: 1,
        });
      }
      setIsTicketModalOpen(true);
    },
    []
  );

  // Close ticket modal
  const closeTicketModal = useCallback(() => {
    setIsTicketModalOpen(false);
    setEditingTicket(null);
  }, []);

  // Handle ticket form changes
  const handleTicketFormChange = useCallback(
    (field: keyof TicketFormData, value: string | number | boolean) => {
      setTicketForm((prev) => ({
        ...prev,
        [field]: value,
      }));
    },
    []
  );

  // Handle multilingual field changes
  const handleMultilingualChange = useCallback(
    (field: "title" | "description", value: Record<string, string>) => {
      setTicketForm((prev) => ({
        ...prev,
        [field]: {
          ...prev[field],
          ...value,
        },
      }));
    },
    []
  );

  // Save ticket
  const handleSaveTicket = useCallback(
    (formData: TicketFormData) => {
      // Prepare ticket data, excluding empty fields
      const ticketData: Partial<EventTicket> = {
        id: editingTicket?.id || `ticket-${Date.now()}`,
        title: formData.title,
        price: formData.isFree ? 0 : formData.price,
        minQuantity: formData.minQuantity,
        currency: eventData.priceCurrency,
        visibility: Number(formData.visibility),
        index: editingTicket?.index || eventData.tickets?.length || 0,
      };

      // Only include description if both bg and en fields have content
      const hasDescription = getLocalizedText(formData.description)?.trim();
      if (hasDescription) {
        ticketData.description = formData.description;
      }

      // Only include expiry date if it's enabled and has a value
      if (formData.hasExpiry && formData.expiresAt) {
        ticketData.expiresAt = formData.expiresAt;
      }

      // Only include start date if it's enabled and has a value
      if (formData.hasStart && formData.startsAt) {
        ticketData.startsAt = formData.startsAt;
      }

      if (editingTicket) {
        updateTicketMutation.mutate(ticketData as EventTicket);
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { id, ...ticketWithoutId } = ticketData;
        createTicketMutation.mutate(ticketWithoutId as Omit<EventTicket, "id">);
      }
    },
    [
      editingTicket,
      eventData.tickets,
      eventData.priceCurrency,
      createTicketMutation,
      updateTicketMutation,
    ]
  );

  // Delete ticket
  const handleDeleteTicket = useCallback(
    (ticketId: string) => {
      deleteTicketMutation.mutate(ticketId);
    },
    [deleteTicketMutation]
  );

  // Handle duplicate ticket
  const handleDuplicateTicket = useCallback(
    (ticket: EventTicket) => {
      const duplicatedTicket = { ...ticket };
      openTicketModal(duplicatedTicket, true);
    },
    [openTicketModal]
  );

  const renderTicketItem = (ticket: EventTicket) => (
    <TicketItem
      ticket={ticket}
      eventId={eventId ?? ""}
      onEdit={openTicketModal}
      onDuplicate={handleDuplicateTicket}
      onDelete={handleDeleteTicket}
      isDeleting={deleteTicketMutation.isPending}
      isReordering={reorderTicketsMutation.isPending}
    />
  );

  const isLoading =
    createTicketMutation.isPending ||
    updateTicketMutation.isPending ||
    deleteTicketMutation.isPending ||
    reorderTicketsMutation.isPending;

  return (
    <>
      {isLoading && <Loading />}

      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="baseline">
          <Text variant="headingMd" as="h3">
            {t("eventDetails.payment.tickets")}
          </Text>
          {!isNewEvent && (
            <Button
              icon={PlusCircleIcon}
              variant="plain"
              onClick={() => openTicketModal()}
              loading={createTicketMutation.isPending}
            >
              {t("eventDetails.payment.addTicket")}
            </Button>
          )}
        </InlineStack>

        {!isNewEvent && 
          eventData.tickets && 
          eventData.tickets.length > 0 && 
          eventData.id && 
          eventData.book === EventBookType.PAY && (
          <TicketShareableLinkBanner
            eventId={eventData.id}
            language={i18n.language}
          />
        )}

        {isNewEvent ? (
          <Box
            borderWidth="025"
            borderColor="border"
            borderRadius="200"
            background="bg-surface-secondary"
            padding="400"
          >
            <Text
              variant="bodyMd"
              tone="subdued"
              alignment="center"
              as="p"
            >
              {t("eventDetails.payment.ticketsAvailableAfterSave")}
            </Text>
          </Box>
        ) : eventData.tickets && eventData.tickets.length > 0 ? (
          <Box
            borderWidth="025"
            borderColor="border"
            borderRadius="200"
            background="bg-surface"
          >
            <SortableList
              items={eventData.tickets}
              onReorderEnd={(reorderedTickets) => {
                // Update via API with just ticket IDs
                const ticketIds = reorderedTickets.map(
                  (ticket) => ticket?.id
                );
                reorderTicketsMutation.mutate(ticketIds);
              }}
              idForItem={(ticket) => ticket?.id}
              renderItem={(item) => renderTicketItem(item)}
              showDividers={true}
            />
          </Box>
        ) : (
          <Banner tone="warning">
            {t("eventDetails.payment.noTickets")}
          </Banner>
        )}
      </BlockStack>


      <TicketModal
        isOpen={isTicketModalOpen}
        onClose={closeTicketModal}
        onSave={handleSaveTicket}
        editingTicket={editingTicket}
        formData={ticketForm}
        onFormChange={handleTicketFormChange}
        onMultilingualChange={handleMultilingualChange}
        eventCurrency={eventData.priceCurrency || EVENT_CURRENCY_DEFAULT}
        loading={
          createTicketMutation.isPending || updateTicketMutation.isPending
        }
      />
    </>
  );
}
